import { FormEvent, useEffect, useId, useRef, useState } from 'react';
import { AuthResponse, gameApi, UserSettings } from '../api';
import { TELEGRAM_BOT_URL } from '../utils/telegram';
import { AuthDialog } from './AuthDialog';

export type AuthFlow = {
  id: number;
  step: 'login' | 'signup' | 'telegram';
  username: string;
  opener: HTMLElement | null;
  destination?: 'multiplayer';
};
type Props = {
  flow: AuthFlow;
  guestUserId: string | null;
  isCurrent: () => boolean;
  onAuthenticated: (auth: AuthResponse, registered: boolean) => void;
  onSettingsUpdated: (settings: UserSettings) => void;
  onComplete: () => void;
  onCancel: () => void;
};

/** One native modal owns credentials and optional onboarding until the flow ends. */
export const AuthPanel = ({ flow, guestUserId, isCurrent, onAuthenticated, onSettingsUpdated, onComplete, onCancel }: Props) => {
  const [step, setStep] = useState(flow.step);
  const [username, setUsername] = useState(flow.username);
  const [password, setPassword] = useState('');
  const [visiblePassword, setVisiblePassword] = useState(false);
  const [touched, setTouched] = useState({ username: false, password: false, telegram: false });
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [telegramId, setTelegramId] = useState('');
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const errorRef = useRef<HTMLDivElement>(null);
  const focusRef = useRef<HTMLElement | null>(null);
  const formId = useId();
  const usernameId = useId(), passwordId = useId(), telegramIdField = useId();
  const mounted = useRef(true);
  const latest = useRef({ isCurrent, onSettingsUpdated });
  latest.current = { isCurrent, onSettingsUpdated };
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);
  const current = () => mounted.current && latest.current.isCurrent();
  const updateStep = (next: 'login' | 'signup' | 'telegram') => {
    flow.step = next;
    setStep(next);
    setPassword('');
    setVisiblePassword(false);
    setTouched({ username: false, password: false, telegram: false });
    setError(null);
    setStatus(next === 'telegram' ? 'Account created. You are logged in.' : '');
  };
  const loadSettings = async () => {
    setLoadingSettings(true);
    setError(null);
    try {
      const result = await gameApi.getPlayerSettings();
      if (current()) setSettings(result);
    } catch (err) {
      if (current()) setError(err instanceof Error ? err.message : 'Could not load notification settings.');
    } finally {
      if (current()) setLoadingSettings(false);
    }
  };
  const submitCredentials = async (event: FormEvent) => {
    event.preventDefault();
    if (pending.current || !username.trim() || !password.trim()) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    setStatus(step === 'signup' ? 'Creating account...' : 'Logging in...');
    const registered = step === 'signup';
    try {
      const auth = registered
        ? await gameApi.signup(username.trim(), password, { language: 'En', sendNotificationsToTelegram: false,
          telegramId: null, sendNotificationsToEmail: false, email: null }, guestUserId)
        : await gameApi.login(username.trim(), password);
      onAuthenticated(auth, registered);
      if (!current()) return;
      flow.username = auth.userName;
      setUsername(auth.userName);
      if (registered) {
        updateStep('telegram');
        void loadSettings();
      } else onComplete();
    } catch (err) {
      if (current()) { setError(err instanceof Error ? err.message : 'Authentication failed.'); setStatus(''); }
    } finally {
      pending.current = false;
      if (current()) setBusy(false);
    }
  };
  const saveTelegram = async (event: FormEvent) => {
    event.preventDefault();
    if (pending.current || !settings || !/^\d+$/.test(telegramId.trim())) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    setStatus('Saving Telegram settings...');
    try {
      const updated = await gameApi.updatePlayerSettings({ ...settings, sendNotificationsToTelegram: true, telegramId: telegramId.trim() });
      latest.current.onSettingsUpdated(updated);
      if (current()) onComplete();
    } catch (err) {
      if (current()) { setError(err instanceof Error ? err.message : 'Could not save Telegram settings.'); setStatus(''); }
    } finally {
      pending.current = false;
      if (current()) setBusy(false);
    }
  };
  const dismiss = step === 'telegram' ? onComplete : onCancel;
  const title = step === 'telegram' ? 'Set up Telegram notifications' : step === 'signup' ? 'Sign Up' : 'Login';
  const usernameError = touched.username && !username.trim() ? 'Enter your username.' : null;
  const passwordError = touched.password && !password.trim() ? 'Enter your password.' : null;
  const telegramError = touched.telegram && !/^\d+$/.test(telegramId.trim()) ? 'Enter a numeric Telegram ID.' : null;
  const actions = <div className="auth-actions">
    <button type="submit" form={formId} className="entry-primary-action" disabled={busy || (step === 'telegram'
      ? loadingSettings || !settings || !/^\d+$/.test(telegramId.trim()) : !username.trim() || !password.trim())}>
      {step === 'telegram' ? 'Save and continue' : step === 'signup' ? 'Create account' : 'Login'}
    </button>
    {(step === 'signup' || step === 'telegram') && <button type="button" className="entry-secondary-action"
      onClick={dismiss} disabled={step === 'signup' && busy}>{step === 'telegram' ? 'Set up later' : 'Cancel'}</button>}
  </div>;
  return <AuthDialog title={title} closeLabel={step === 'telegram' ? 'Close Telegram setup' : step === 'signup' ? 'Close sign up' : 'Close login'}
    className={step === 'signup' ? 'signup-popup-content' : step === 'login' ? 'login-popup-content' : 'telegram-popup-content'}
    onClose={dismiss} initialFocusRef={focusRef} focusKey={step} opener={flow.opener} footer={actions}>
    {error && <div className="error auth-error" role="alert" tabIndex={-1} ref={errorRef}>{error}</div>}
    <div className="auth-status" role="status" aria-live="polite">{status}</div>
    {step === 'telegram' ? <>
      <p>Telegram notifications are optional. You can change them later in account settings.</p>
      <p className="telegram-notifications-hint"><a href={TELEGRAM_BOT_URL} target="_blank" rel="noreferrer">Open Telegram bot</a> and press Start to get your Telegram ID.</p>
      <form id={formId} className="auth-form" onSubmit={saveTelegram} aria-busy={busy || loadingSettings} noValidate>
        <div className="settings-field">
          <label htmlFor={telegramIdField}>Telegram ID</label>
          <p id={telegramIdField + '-help'} className="telegram-hint">Enter your numeric Telegram ID, not your @username.</p>
          <input id={telegramIdField} name="telegramId" inputMode="numeric" value={telegramId} disabled={busy}
            onChange={event => setTelegramId(event.target.value)} onBlur={() => setTouched(value => ({ ...value, telegram: true }))}
            aria-invalid={!!telegramError} aria-describedby={telegramIdField + '-help' + (telegramError ? ' ' + telegramIdField + '-error' : '')} />
          {telegramError && <div className="error" id={telegramIdField + '-error'}>{telegramError}</div>}
        </div>
      </form>
      {loadingSettings && <p role="status">Loading notification settings...</p>}
      {!loadingSettings && !settings && <button type="button" className="entry-secondary-action" onClick={() => void loadSettings()}>Retry</button>}
    </> : <>
      <form id={formId} className="auth-form" onSubmit={submitCredentials} aria-busy={busy} noValidate>
        <div className="settings-field">
          <label htmlFor={usernameId}>Username</label>
          <input id={usernameId} ref={node => { focusRef.current = node; }} name="username" autoComplete="username" autoCapitalize="none" spellCheck={false}
            value={username} disabled={busy} onChange={event => { setUsername(event.target.value); flow.username = event.target.value; }}
            onBlur={() => setTouched(value => ({ ...value, username: true }))} aria-invalid={!!usernameError}
            aria-describedby={usernameError ? usernameId + '-error' : undefined} />
          {usernameError && <div className="error" id={usernameId + '-error'}>{usernameError}</div>}
        </div>
        <div className="settings-field">
          <label htmlFor={passwordId}>Password</label>
          <div className="auth-password-control">
            <input id={passwordId} name="password" autoComplete={step === 'signup' ? 'new-password' : 'current-password'} type={visiblePassword ? 'text' : 'password'}
              value={password} disabled={busy} onChange={event => setPassword(event.target.value)}
              onBlur={() => setTouched(value => ({ ...value, password: true }))} aria-invalid={!!passwordError}
              aria-describedby={passwordError ? passwordId + '-error' : undefined} />
            <button type="button" className="auth-text-action" aria-pressed={visiblePassword} disabled={busy}
              onClick={() => setVisiblePassword(value => !value)}>{visiblePassword ? 'Hide password' : 'Show password'}</button>
          </div>
          {passwordError && <div className="error" id={passwordId + '-error'}>{passwordError}</div>}
        </div>
      </form>
      <button type="button" className="auth-text-action auth-switch" disabled={busy} onClick={() => updateStep(step === 'login' ? 'signup' : 'login')}>
        {step === 'login' ? 'New here? Create an account' : 'Already have an account? Log in'}
      </button>
    </>}
  </AuthDialog>;
};
